import {
  REPORTING_API_V1_BASE_METRICS,
  ReportingApiRow,
  ReportingApiV1BreakdownRow,
  ReportingApiV1ConversionsByBeaconRow,
  ReportingApiV1MetricSlice,
  ReportingApiV1ReportMetrics,
  ReportingApiV1SelectionFilters,
  ReportingApiV1TablePoint,
  ReportingApiV1TimeSeriesPoint,
} from './reporting-api-v1.types';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const emptyMetricSlice = (startDate?: string, endDate?: string): ReportingApiV1MetricSlice => ({
  impressions: 0,
  clicks: 0,
  views: 0,
  spend: 0,
  conversions: 0,
  reach: 0,
  results: null,
  ctr: 0,
  cpc: 0,
  cpm: 0,
  cpa: 0,
  video_starts: 0,
  video_completes: 0,
  audio_starts: 0,
  audio_completes: 0,
  start_date: startDate,
  end_date: endDate,
});

export const parseLocalDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Prior window of equal length ending the day before `startDate`. */
export const computePriorDateRange = (
  startDate: string,
  endDate: string
): { startDate: string; endDate: string } => {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  const durationDays = Math.max(
    1,
    Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1
  );
  const pastEnd = new Date(start);
  pastEnd.setDate(pastEnd.getDate() - 1);
  const pastStart = new Date(pastEnd);
  pastStart.setDate(pastStart.getDate() - (durationDays - 1));
  return {
    startDate: formatLocalDate(pastStart),
    endDate: formatLocalDate(pastEnd),
  };
};

export const buildReportingApiFilters = (
  selection: ReportingApiV1SelectionFilters
): Record<string, string[]> => {
  const filters: Record<string, string[]> = {
    source: [selection.source],
  };
  if (selection.accountIds.length) {
    filters.advertiser_id = selection.accountIds.map(String);
  }
  if (selection.campaignIds?.length) {
    filters.campaign_id = selection.campaignIds.map(String);
  }
  if (selection.lineItemIds?.length) {
    filters.line_item_id = selection.lineItemIds.map(String);
  }
  return filters;
};

export const rowToMetricSlice = (
  row: ReportingApiRow | undefined,
  startDate?: string,
  endDate?: string
): ReportingApiV1MetricSlice => {
  if (!row) return emptyMetricSlice(startDate, endDate);
  return {
    ...emptyMetricSlice(startDate, endDate),
    impressions: toNumber(row.impressions),
    clicks: toNumber(row.clicks),
    spend: toNumber(row.spend),
    conversions: toNumber(row.conversions),
    reach: toNumber(row.reach),
    results: row.results == null ? null : toNumber(row.results),
    ctr: toNumber(row.ctr),
    cpc: toNumber(row.cpc),
    cpm: toNumber(row.cpm),
    cpa: toNumber(row.cpa),
    // API exposes video_completions; portal UI uses video_completes.
    video_starts: toNumber(row.video_starts),
    video_completes: toNumber(row.video_completions ?? row.video_completes),
    audio_starts: toNumber(row.audio_starts),
    audio_completes: toNumber(row.audio_completes),
    start_date: startDate,
    end_date: endDate,
  };
};

const DIF_METRIC_KEYS = [
  ...(REPORTING_API_V1_BASE_METRICS as readonly string[]),
  'reach',
  'video_starts',
  'video_completes',
  'audio_starts',
  'audio_completes',
] as const;

export const buildReportMetrics = (
  currentRow: ReportingApiRow | undefined,
  pastRow: ReportingApiRow | undefined,
  currentRange: { startDate: string; endDate: string },
  pastRange: { startDate: string; endDate: string }
): ReportingApiV1ReportMetrics => {
  const current = rowToMetricSlice(currentRow, currentRange.startDate, currentRange.endDate);
  const past = rowToMetricSlice(pastRow, pastRange.startDate, pastRange.endDate);
  const dif = emptyMetricSlice();
  DIF_METRIC_KEYS.forEach((key) => {
    dif[key] = toNumber(current[key]) - toNumber(past[key]);
  });
  if (current.results != null || past.results != null) {
    dif.results = toNumber(current.results) - toNumber(past.results);
  }
  return { current, past, dif };
};

export const rowsToTimeSeries = (rows: ReportingApiRow[]): ReportingApiV1TimeSeriesPoint[] =>
  rows.map((row) => ({
    date: String(row.date ?? ''),
    impressions: toNumber(row.impressions),
    clicks: toNumber(row.clicks),
    views: 0,
    interactions: 0,
    spend: toNumber(row.spend),
    conversions: toNumber(row.conversions),
    video_starts: toNumber(row.video_starts),
    video_completes: toNumber(row.video_completions ?? row.video_completes),
    audio_starts: toNumber(row.audio_starts),
    audio_completes: toNumber(row.audio_completes),
    ctr: toNumber(row.ctr),
    cpm: toNumber(row.cpm),
    cpa: toNumber(row.cpa),
    cpc: toNumber(row.cpc),
  }));

const baseMetricsFromRow = (row: ReportingApiRow) => ({
  spend: toNumber(row.spend),
  impressions: toNumber(row.impressions),
  clicks: toNumber(row.clicks),
  conversions: toNumber(row.conversions),
  ctr: toNumber(row.ctr),
  cpc: toNumber(row.cpc),
  cpm: toNumber(row.cpm),
  cpa: toNumber(row.cpa),
});

export const rowsToZipcodeTable = (rows: ReportingApiRow[]): ReportingApiV1TablePoint[] =>
  rows.reduce<ReportingApiV1TablePoint[]>((acc, row) => {
    // Prefer public alias; fall back to mart column / legacy anonymous keys.
    const zipcode = String(row.zipcode ?? row.zip ?? row.f0_ ?? '');
    if (!zipcode) return acc;
    const point: ReportingApiV1TablePoint = {
      ...baseMetricsFromRow(row),
      key: zipcode,
      zipcode,
    };
    acc.push(point);
    return acc;
  }, []);

export const rowsToDomainTable = (rows: ReportingApiRow[]): ReportingApiV1TablePoint[] =>
  rows.reduce<ReportingApiV1TablePoint[]>((acc, row) => {
    // Prefer public alias; fall back to anonymous BigQuery keys from older SQL.
    const domain = String(row.domain ?? row.f0_ ?? '');
    if (!domain) return acc;
    const point: ReportingApiV1TablePoint = {
      ...baseMetricsFromRow(row),
      key: domain,
      domain,
    };
    acc.push(point);
    return acc;
  }, []);

export const rowsToAppTable = (rows: ReportingApiRow[]): ReportingApiV1TablePoint[] =>
  rows.reduce<ReportingApiV1TablePoint[]>((acc, row) => {
    const app = String(row.app_name ?? row.app_id ?? '');
    if (!app) return acc;
    const point: ReportingApiV1TablePoint = {
      ...baseMetricsFromRow(row),
      key: app,
      app,
    };
    acc.push(point);
    return acc;
  }, []);

export const rowsToBreakdown = (rows: ReportingApiRow[]): ReportingApiV1BreakdownRow[] =>
  rows.map((row) => {
    const campaignIdRaw = row.campaign_id ?? row.f0_;
    const campaignId =
      typeof campaignIdRaw === 'number'
        ? campaignIdRaw
        : Number.parseInt(String(campaignIdRaw ?? '0'), 10) || 0;
    return {
      date: String(row.date ?? ''),
      campaign_name: String(row.campaign_name ?? row.campaign_id ?? row.f0_ ?? 'Unknown'),
      campaign_id: campaignId,
      line_item_name: null,
      line_item_id: null,
      creative_name: null,
      creative_id: null,
      impressions: toNumber(row.impressions),
      clicks: toNumber(row.clicks),
      views: 0,
      interactions: 0,
      spend: toNumber(row.spend),
      conversions: toNumber(row.conversions),
      video_starts: toNumber(row.video_starts),
      video_completes: toNumber(row.video_completions ?? row.video_completes),
      audio_starts: toNumber(row.audio_starts),
      audio_completes: toNumber(row.audio_completes),
    };
  });

export const rowsToConversionsByBeacon = (
  rows: ReportingApiRow[]
): ReportingApiV1ConversionsByBeaconRow[] =>
  rows.map((row) => ({
    conversion_id: String(row.conversion_id ?? ''),
    conversion_name:
      row.conversion_name == null ? null : String(row.conversion_name),
    conversion_type:
      row.conversion_type == null ? null : String(row.conversion_type),
    conversions: toNumber(row.conversions),
    conversion_value: toNumber(row.conversion_value),
    view_through_conversions: toNumber(row.view_through_conversions),
  }));
